import React, { useEffect, useMemo, useRef, useContext } from "react";
import { applyTravelTemplate } from "src/helpers/applyTemplate";
import { AuthContext } from "src/context/auth";

export default function TravelCustomCssPreview({ template, pages, onPageClick, className, autoResize = false, forceTailwind = false, tailwindCdn = "https://cdn.tailwindcss.com", bannerImage, footerImage }) {
  const iframeRef = useRef(null);
  const onPageClickRef = useRef(onPageClick);
  const { user } = useContext(AuthContext);
  const userName = user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() : "";
  useEffect(() => {
    onPageClickRef.current = onPageClick;
  }, [onPageClick]);

  const html = useMemo(() => {
    if (!template) return "";
    try {
      const safePages = Array.isArray(pages) ? pages : [];

      return applyTravelTemplate(template, safePages, userName, bannerImage, footerImage) || "";
    } catch {
      return "";
    }
  }, [template, pages, userName, bannerImage, footerImage]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const isExternal = /^https?:\/\//i.test(template?.trim?.());
    if (isExternal) {
      iframe.removeAttribute("srcdoc");
      iframe.src = template;
      return;
    }

    const isFullDoc = /<\s*html[\s>]/i.test(html);
    const containsTailwind = (s) => /cdn\.tailwindcss\.com/i.test(s) || /tailwind(\.min)?\.css/i.test(s);
    const likelyUsesTWClasses = (s) => /\b(?:flex|grid|hidden|block|inline|p-\d|px-\d|py-\d|m-\d|rounded|shadow|text-[\w:/\-[\]]+|bg-[\w:/\-[\]]+)\b/.test(s);

    let finalHtml = html;
    if (isFullDoc) {
      if (!containsTailwind(html) && (forceTailwind || likelyUsesTWClasses(html))) {
        finalHtml = html.replace(/<\s*head(\s[^>]*)?>/i, (m) => `${m}\n<script data-injected="tw" src="${tailwindCdn}"></script>`);
      }
    } else {
      const needsTW = forceTailwind || likelyUsesTWClasses(html);
      const twScript = needsTW ? `<script id="_tw-cdn" src="${tailwindCdn}"></script>` : "";
      finalHtml = `
<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width,initial-scale=1" />
    <base target="_blank" />
    ${twScript}
    <style>
      html, body { margin: 0; padding: 0; }
      img, video { max-width: 100%; height: auto; }
    </style>
  </head>
  <body>${html}</body>
</html>`;
    }

    try {
      const doc = iframe.contentDocument;
      if (!doc) return;
      doc.open();
      doc.write(finalHtml);
      doc.close();

      const clickHandler = (e) => {
        let el = e.target;
        while (el && el !== doc.body) {
          const pageType = el.getAttribute?.("data-page-type");
          const pageLink = el.getAttribute?.("data-page-link");
          const pageLinkNewtab = el.getAttribute?.("data-page-link-newtab");
          const slug = el.getAttribute?.("data-page-slug");

          if (pageType === "EXTERNAL_LINK" && pageLink) {
            e.preventDefault?.();
            const target = pageLinkNewtab === "false" ? "_self" : "_blank";
            window.open(pageLink, target);
            return;
          }

          if (slug) {
            e.preventDefault?.();
            doc.querySelectorAll("[data-page-slug]").forEach((n) => n.classList.remove("active"));
            el.classList.add("active");
            onPageClickRef.current?.(slug);
            return;
          }
          el = el.parentElement;
        }
      };
      doc.addEventListener("click", clickHandler);

      let ro;
      let frameId;
      const setHeight = () => {
        if (frameId) cancelAnimationFrame(frameId);
        frameId = requestAnimationFrame(() => {
          const iframeCurrent = iframeRef.current;
          if (!iframeCurrent) return;
          const iframeDoc = iframeCurrent.contentDocument;
          if (!iframeDoc || !iframeDoc.body) return;
          const h = Math.max(iframeDoc.body.scrollHeight, iframeDoc.documentElement.scrollHeight, iframeDoc.body.offsetHeight, iframeDoc.documentElement.offsetHeight, iframeDoc.body.clientHeight, iframeDoc.documentElement.clientHeight);
          iframeCurrent.style.height = `${h}px`;
        });
      };

      if (autoResize) {
        setHeight();
        if (typeof ResizeObserver !== "undefined") {
          ro = new ResizeObserver(setHeight);
          ro.observe(doc.body);
        } else {
          doc.defaultView?.addEventListener("resize", setHeight);
          setTimeout(setHeight, 100);
        }
      }

      return () => {
        doc.removeEventListener("click", clickHandler);
        if (ro) ro.disconnect();
        if (frameId) cancelAnimationFrame(frameId);
        if (autoResize) doc.defaultView?.removeEventListener("resize", setHeight);
      };
    } catch (err) {
      console.warn("Iframe write or resize failed:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html, template, autoResize, forceTailwind, tailwindCdn]);

  return (
    <iframe
      ref={iframeRef}
      className={className}
      sandbox="allow-same-origin allow-scripts allow-popups allow-top-navigation-by-user-activation"
      srcDoc="<!doctype html><html><head></head><body></body></html>"
      style={{ width: "100%", height: "100%", border: 0 }}
      title="Travel Page"
    />
  );
}
